// src/pages/portal/PortalConversacionPage.tsx
import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortalConversacion, useEnviarPortalMensaje } from '@/hooks/queries';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Skeleton,
  Avatar,
} from '@/components/ui';
import { ArrowLeft, Send, Paperclip } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

function ConversacionSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-[500px]" />
    </div>
  );
}

export default function PortalConversacionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversacion, isLoading, isError } = usePortalConversacion(id);
  const enviarMensaje = useEnviarPortalMensaje();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversacion?.mensajes]);

  const handleEnviar = () => {
    if (!nuevoMensaje.trim() || !id) return;

    enviarMensaje.mutate(
      { conversacionId: id, data: { contenido: nuevoMensaje } },
      {
        onSuccess: () => setNuevoMensaje(''),
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  if (isLoading) {
    return <ConversacionSkeleton />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error al cargar la conversacion</p>
        <Button className="mt-4" onClick={() => navigate('/portal/conversaciones')}>
          Volver
        </Button>
      </div>
    );
  }

  if (!conversacion) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          Conversacion no encontrada
        </p>
        <Button className="mt-4" onClick={() => navigate('/portal/conversaciones')}>
          Volver
        </Button>
      </div>
    );
  }

  const estadoBadgeVariant = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'activa':
      case 'abierta':
        return 'success';
      case 'pausada':
        return 'warning';
      case 'cerrada':
        return 'default';
      default:
        return 'default';
    }
  };

  const isClosed = conversacion.estado?.toLowerCase() === 'cerrada';

  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/portal/conversaciones')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
        <div className="flex-1">
          <h1 className="font-semibold text-gray-900 dark:text-white">
            {conversacion.asunto || 'Conversación'}
          </h1>
        </div>
        <Badge variant={estadoBadgeVariant(conversacion.estado)}>
          {conversacion.estado}
        </Badge>
      </div>

      {/* Chat container */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        {/* Mensajes */}
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {conversacion.mensajes.map((mensaje, index) => {
            const showDate =
              index === 0 ||
              format(new Date(mensaje.fecha), 'yyyy-MM-dd') !==
                format(new Date(conversacion.mensajes[index - 1].fecha), 'yyyy-MM-dd');

            return (
              <div key={mensaje.id}>
                {showDate && (
                  <div className="flex justify-center my-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                      {format(new Date(mensaje.fecha), 'EEEE, d MMMM', { locale: es })}
                    </span>
                  </div>
                )}

                <div
                  className={cn(
                    'flex gap-3',
                    mensaje.autor.es_soporte ? 'justify-start' : 'justify-end'
                  )}
                >
                  {mensaje.autor.es_soporte && (
                    <Avatar fallback={mensaje.autor.nombre} size="sm" />
                  )}

                  <div
                    className={cn(
                      'max-w-[70%] rounded-lg px-4 py-2',
                      mensaje.autor.es_soporte
                        ? 'bg-gray-100 dark:bg-gray-800'
                        : 'bg-blue-600 text-white'
                    )}
                  >
                    {mensaje.autor.es_soporte && (
                      <p
                        className={cn(
                          'text-xs font-medium mb-1',
                          'text-gray-600 dark:text-gray-400'
                        )}
                      >
                        {mensaje.autor.nombre}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap">{mensaje.contenido}</p>

                    {mensaje.adjuntos && mensaje.adjuntos.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {mensaje.adjuntos.map((adj) => (
                          <div
                            key={adj.id}
                            className="flex items-center gap-2 text-sm opacity-80"
                          >
                            <Paperclip className="w-3 h-3" />
                            <span>{adj.nombre}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <p
                      className={cn(
                        'text-xs mt-1',
                        mensaje.autor.es_soporte
                          ? 'text-gray-500 dark:text-gray-400'
                          : 'text-blue-100'
                      )}
                    >
                      {format(new Date(mensaje.fecha), 'HH:mm')}
                    </p>
                  </div>

                  {!mensaje.autor.es_soporte && (
                    <Avatar fallback={mensaje.autor.nombre} size="sm" />
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input de mensaje */}
        {!isClosed && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-2">
              <textarea
                value={nuevoMensaje}
                onChange={(e) => setNuevoMensaje(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe un mensaje..."
                className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={1}
              />
              <Button
                onClick={handleEnviar}
                disabled={!nuevoMensaje.trim() || enviarMensaje.isPending}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {isClosed && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400">
            Esta conversacion esta cerrada
          </div>
        )}
      </Card>
    </div>
  );
}
